import React, { useContext, useEffect, useState, useRef } from 'react'
import { message } from 'antd'
import { AppContext } from 'AppContext'
import DocumentViewerModal from 'components/DocumentViewerModal'
import { canUserDownload } from 'utils/User'
import moment from 'moment'

export default function AuditSecurityGuard() {
  const { state } = useContext(AppContext) || { state: {} }
  const isAudit = state?.isAudit || state?.currentUser?.role === 'AUDIT'
  const canDownload = canUserDownload(state?.userPermissions || state?.currentUser, state?.isClient)

  const [docViewer, setDocViewer] = useState({
    visible: false,
    fileUrl: '',
    files: [],
    title: '',
  })
  const [isWindowBlurred, setIsWindowBlurred] = useState(false)
  const lastNotifyTimeRef = useRef(0)

  useEffect(() => {
    // Global event listener for secure document viewing
    const handleOpenSecureDoc = (e) => {
      const { fileUrl, files, title } = e.detail || {}
      if (fileUrl || (files && files.length > 0)) {
        setDocViewer({
          visible: true,
          fileUrl: fileUrl || (files && files[0]),
          files: files || (fileUrl ? [fileUrl] : []),
          title: title || 'Document Preview',
        })
      }
    }
    window.addEventListener('OPEN_SECURE_DOC', handleOpenSecureDoc)

    // Global document link interception for unauthorized downloaders (e.g. AUDIT)
    const handleDocumentLinkClick = (e) => {
      if (canDownload && !isAudit) return

      const anchor = e.target && e.target.closest ? e.target.closest('a') : null
      if (!anchor) return

      const href = anchor.getAttribute('href')
      const hasDownload = anchor.hasAttribute('download')

      if (
        !href ||
        href.startsWith('#') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        // eslint-disable-next-line no-script-url
        href.startsWith('javascript:')
      ) {
        return
      }

      // Never intercept internal single-page application routes (e.g. /agreement-format, /edit-agreement, /invoice)
      const isInternalSpaRoute =
        href.startsWith('/') &&
        !href.startsWith('//') &&
        !/\.(pdf|png|jpe?g|webp|svg|gif|docx?|xlsx?)($|\?)/i.test(href) &&
        !href.startsWith('/uploads/') &&
        !href.startsWith('/kyc/')

      if (isInternalSpaRoute) {
        return
      }

      const isDoc =
        hasDownload ||
        /\.(pdf|png|jpe?g|webp|svg|gif)($|\?)/i.test(href) ||
        href.includes('/kyc/') ||
        href.includes('incomeProof') ||
        href.includes('invoicePdf') ||
        (href.includes('agreement') &&
          (hasDownload ||
            /\.(pdf|png|jpe?g)($|\?)/i.test(href) ||
            href.startsWith('http') ||
            href.startsWith('blob:')))

      if (isDoc) {
        e.preventDefault()
        e.stopPropagation()
        setDocViewer({
          visible: true,
          fileUrl: href,
          title: anchor.innerText?.trim() || anchor.getAttribute('title') || 'Document Preview',
        })
      }
    }

    document.addEventListener('click', handleDocumentLinkClick, true)

    return () => {
      window.removeEventListener('OPEN_SECURE_DOC', handleOpenSecureDoc)
      document.removeEventListener('click', handleDocumentLinkClick, true)
    }
  }, [canDownload, isAudit])

  useEffect(() => {
    if (!isAudit) return

    // Notify user that screenshot is prohibited (throttled)
    const notifyScreenshotBlocked = () => {
      const now = Date.now()
      if (now - lastNotifyTimeRef.current > 2500) {
        lastNotifyTimeRef.current = now
        message.warning({
          content: 'Screenshots and screen recording are disabled for Audit accounts.',
          key: 'audit-screenshot-guard',
          duration: 3,
        })
      }
    }

    // Helper: Identify screenshot shortcut key combinations across Windows, Mac, Linux
    const isScreenshotShortcut = (e) => {
      const key = e.key ? e.key.toLowerCase() : ''
      const code = e.code || ''
      const keyCode = e.keyCode || e.which

      // 1. PrintScreen key (Windows & Linux - keyCode 44)
      if (
        key === 'printscreen' ||
        code === 'PrintScreen' ||
        keyCode === 44 ||
        key === 'snapshot'
      ) {
        return true
      }

      // 2. Windows Snipping Tool (Win + Shift + S / Ctrl + Shift + S / Alt + Shift + S)
      if (
        (e.ctrlKey || e.metaKey || e.altKey) &&
        e.shiftKey &&
        (key === 's' || code === 'KeyS' || keyCode === 83)
      ) {
        return true
      }

      // 3. Mac OS Screen Capture Shortcuts (Cmd + Shift + 3 / 4 / 5 / 6)
      if (
        e.metaKey &&
        e.shiftKey &&
        (['3', '4', '5', '6'].includes(key) ||
          ['Digit3', 'Digit4', 'Digit5', 'Digit6'].includes(code) ||
          [51, 52, 53, 54].includes(keyCode))
      ) {
        return true
      }

      // 4. Mac OS Screen Capture to Clipboard (Cmd + Ctrl + Shift + 3 / 4)
      if (
        e.metaKey &&
        e.ctrlKey &&
        e.shiftKey &&
        (['3', '4'].includes(key) ||
          ['Digit3', 'Digit4'].includes(code) ||
          [51, 52].includes(keyCode))
      ) {
        return true
      }

      // 5. Windows Game Bar / Capture (Win + G / Win + Alt + PrtScn)
      if (e.metaKey && (key === 'g' || code === 'KeyG' || keyCode === 71)) {
        return true
      }

      return false
    }

    const handleScreenshotAttempt = (e) => {
      e.preventDefault()
      e.stopPropagation()

      // Wipe system clipboard immediately
      try {
        if (navigator?.clipboard?.writeText) {
          navigator.clipboard.writeText('').catch(() => {})
        }
      } catch (err) {}

      // Temporarily blur screen to protect against background buffer captures
      setIsWindowBlurred(true)
      document.body.classList.add('audit-blurred')
      setTimeout(() => {
        if (document.hasFocus()) {
          setIsWindowBlurred(false)
          document.body.classList.remove('audit-blurred')
        }
      }, 1200)

      notifyScreenshotBlocked()
    }

    // 1. Keyboard shortcut blocking on keydown (capture phase)
    const handleKeyDown = (e) => {
      if (isScreenshotShortcut(e)) {
        handleScreenshotAttempt(e)
        return false
      }

      const key = e.key ? e.key.toLowerCase() : ''
      const isCtrlOrCmd = e.ctrlKey || e.metaKey

      // Block F12 (DevTools)
      if (e.key === 'F12' || e.keyCode === 123) {
        e.preventDefault()
        e.stopPropagation()
        return false
      }

      // Block Ctrl+C (Copy), Ctrl+P (Print), Ctrl+V (Paste), Ctrl+A (Select All), Ctrl+S (Save), Ctrl+U (View Source)
      if (isCtrlOrCmd && ['c', 'p', 'v', 'a', 's', 'u'].includes(key)) {
        e.preventDefault()
        e.stopPropagation()
        return false
      }

      // Block Inspect Element combinations: Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C (and Cmd+Option+I/J/C on Mac)
      if ((isCtrlOrCmd || e.altKey) && e.shiftKey && ['i', 'j', 'c'].includes(key)) {
        e.preventDefault()
        e.stopPropagation()
        return false
      }

      // Mac Cmd+Option+I / Cmd+Option+J / Cmd+Option+C
      if (e.metaKey && e.altKey && ['i', 'j', 'c'].includes(key)) {
        e.preventDefault()
        e.stopPropagation()
        return false
      }
    }

    // 2. Intercept keyup: On Windows, PrintScreen often fires on keyup
    const handleKeyUp = (e) => {
      if (isScreenshotShortcut(e)) {
        handleScreenshotAttempt(e)
        return false
      }
    }

    // 3. Anti-Snipping Tool Obfuscation: Blur screen whenever window loses focus (e.g. Snipping tool opened)
    let blurTimer = null
    const handleWindowBlur = () => {
      blurTimer = setTimeout(() => {
        if (!document.hasFocus()) {
          setIsWindowBlurred(true)
          document.body.classList.add('audit-blurred')
        }
      }, 150)
    }

    const handleWindowFocus = () => {
      if (blurTimer) clearTimeout(blurTimer)
      setIsWindowBlurred(false)
      document.body.classList.remove('audit-blurred')
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsWindowBlurred(true)
        document.body.classList.add('audit-blurred')
      } else if (document.hasFocus()) {
        setIsWindowBlurred(false)
        document.body.classList.remove('audit-blurred')
      }
    }

    // 4. Block Right-Click Context Menu
    const handleContextMenu = (e) => {
      e.preventDefault()
      e.stopPropagation()
      return false
    }

    // 5. Block Clipboard events directly (copy, cut, paste)
    const handleClipboard = (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (e.clipboardData) {
        e.clipboardData.clearData()
      }
      return false
    }

    // 6. Block Drag / Drop of text or media
    const handleDragStart = (e) => {
      e.preventDefault()
      e.stopPropagation()
      return false
    }

    // 7. Add security class to body for CSS user-select blocking & print suppression
    document.body.classList.add('audit-security-mode')

    window.addEventListener('keydown', handleKeyDown, true)
    window.addEventListener('keyup', handleKeyUp, true)
    window.addEventListener('blur', handleWindowBlur)
    window.addEventListener('focus', handleWindowFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('contextmenu', handleContextMenu, true)
    document.addEventListener('copy', handleClipboard, true)
    document.addEventListener('cut', handleClipboard, true)
    document.addEventListener('paste', handleClipboard, true)
    document.addEventListener('dragstart', handleDragStart, true)

    return () => {
      if (blurTimer) clearTimeout(blurTimer)
      document.body.classList.remove('audit-security-mode')
      document.body.classList.remove('audit-blurred')
      window.removeEventListener('keydown', handleKeyDown, true)
      window.removeEventListener('keyup', handleKeyUp, true)
      window.removeEventListener('blur', handleWindowBlur)
      window.removeEventListener('focus', handleWindowFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('contextmenu', handleContextMenu, true)
      document.removeEventListener('copy', handleClipboard, true)
      document.removeEventListener('cut', handleClipboard, true)
      document.removeEventListener('paste', handleClipboard, true)
      document.removeEventListener('dragstart', handleDragStart, true)
    }
  }, [isAudit])

  const auditUserName =
    state?.currentUser?.userName || state?.currentUser?.name || 'AUDIT'
  const currentDate = moment().format('DD-MMM-YYYY')

  return (
    <>
      {/* Screen Shield when OS screen snipping tool is triggered or window loses focus */}
      {isAudit && isWindowBlurred && (
        <div className='audit-screen-shield' aria-hidden='true'>
          <div className='audit-screen-shield-content'>
            <div className='audit-shield-icon'>
              <span role='img' aria-label='lock'>
                🔒
              </span>
            </div>
            <div className='audit-shield-title'>Confidential Audit Session</div>
            <div className='audit-shield-desc'>
              Screen capture is strictly prohibited. Please click on this window to resume your session.
            </div>
          </div>
        </div>
      )}

      <DocumentViewerModal
        visible={docViewer.visible}
        fileUrl={docViewer.fileUrl}
        files={docViewer.files}
        title={docViewer.title}
        onClose={() =>
          setDocViewer({ visible: false, fileUrl: '', files: [], title: '' })
        }
        canDownload={canDownload}
        watermark={
          !canDownload
            ? `CONFIDENTIAL AUDIT • ${auditUserName} • ${currentDate}`
            : ''
        }
      />
    </>
  )
}

